"use client";

import { useEffect, useRef, useState } from "react";
import { DARK_BAND, GradText, SectionHead, SectionShell } from "./kit";
import { gsap, NOT_REDUCED, ScrollTrigger, useGSAP } from "./kit/motion";

const STEPS = [
  {
    id: "device",
    rule: "IF DEVICE",
    match: "iOS → App Store",
    title: "First stop: the device rule.",
    desc: "iPhones head straight to the App Store. Our visitor is on Android, so this rule waves them through to the next check — no dead end, just the next rule in line.",
    verdict: "no match · pass",
  },
  {
    id: "geo",
    rule: "IF COUNTRY",
    match: "FR → /fr landing",
    title: "Next: where in the world?",
    desc: "Geo rules serve localized pages and keep campaigns compliant. This one only fires for visitors in France — our Berliner is passed down the chain.",
    verdict: "no match · pass",
  },
  {
    id: "time",
    rule: "IF TIME WINDOW",
    match: "Sat–Sun → /weekend-promo",
    title: "Then: is the promo live?",
    desc: "Time windows flip destinations on schedule — weekend deals, office hours, late-night offers. It's 23:12 on a Tuesday. Pass.",
    verdict: "no match · pass",
  },
  {
    id: "default",
    rule: "DEFAULT",
    match: "→ myshop.com",
    title: "The required default catches everyone.",
    desc: "No rule matched — and nothing broke. Every rii.link ships with a required default, so no combination of device, place or time ever hits a dead end.",
    verdict: "matched · 302",
  },
];

/** Node surface colors, also used as the tween targets in the scrub. */
const NODE_BG = "#1b1b26";
const NODE_BG_ACTIVE = "#242432";
const NODE_BORDER = "rgba(255,255,255,0.12)";
const NODE_BORDER_ACTIVE = "rgba(250,67,127,0.65)";

/**
 * "Follow one visitor" — the page's signature scroll moment. Sticky viewport
 * + ScrollTrigger scrub (NEVER pin: true — pin-spacers fight RowBand
 * row-snapping and grid-relayout measurement). Dark bands are opaque, so
 * nothing inside the sticky track is a [data-grid-block]; the chain nodes
 * carry their own opaque surfaces instead.
 */
export default function PinnedRouteStorySection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const chainRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  // Scrub layout only where it can run: md+ pointer scroll, motion allowed.
  // Default (SSR, mobile, reduced motion) is the static stacked story.
  const [enhanced, setEnhanced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`${NOT_REDUCED} and (min-width: 768px)`);
    const update = () => setEnhanced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // The track grows to 300vh when enhanced — tell RowBand/GridClearing and
  // ScrollTrigger the geometry changed. Skip the very first run: nothing has
  // changed yet on mount, so no relayout/refresh is needed.
  const isFirstEnhancedRun = useRef(true);
  useEffect(() => {
    if (isFirstEnhancedRun.current) {
      isFirstEnhancedRun.current = false;
      return;
    }
    window.dispatchEvent(new Event("grid-relayout"));
    ScrollTrigger.refresh();
  }, [enhanced]);

  useGSAP(
    (_context, contextSafe) => {
      if (!enhanced) return;
      const track = trackRef.current;
      const chain = chainRef.current;
      const copyWrap = copyRef.current;
      if (!track || !chain || !copyWrap) return;

      // Node geometry (offsetTop/offsetHeight) must be measured after webfonts
      // settle, or the packet lands on pre-reflow positions. revertOnUpdate
      // means a stale run's build can still resolve after enhanced flips again
      // — guard with `disposed`, set by the cleanup below, and register the
      // async-created timeline with the context via contextSafe.
      let disposed = false;
      let tl: gsap.core.Timeline | undefined;

      const build = contextSafe!(() => {
        if (disposed) return;

        const packet = chain.querySelector<HTMLElement>("[data-story-packet]");
        const nodes = gsap.utils.toArray<HTMLElement>("[data-story-node]", chain);
        const verdicts = gsap.utils.toArray<HTMLElement>("[data-story-verdict]", chain);
        const copies = gsap.utils.toArray<HTMLElement>("[data-story-copy]", copyWrap);
        if (
          !packet ||
          nodes.length !== STEPS.length ||
          verdicts.length !== STEPS.length ||
          copies.length !== STEPS.length
        )
          return;

        gsap.set(verdicts, { opacity: 0 });
        gsap.set(copies.slice(1), { opacity: 0 });

        tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: track,
            start: "top top",
            end: "bottom bottom",
            scrub: 0.6,
          },
        });

        STEPS.forEach((_step, i) => {
          const node = nodes[i];
          // Packet rides the chain's left rail; land its center on the node's.
          const y = node.offsetTop + node.offsetHeight / 2 - 6;
          tl!.to(packet, { y, duration: 1 }, i);
          tl!.to(
            node,
            { borderColor: NODE_BORDER_ACTIVE, backgroundColor: NODE_BG_ACTIVE, duration: 0.25 },
            i + 0.35,
          );
          tl!.to(verdicts[i], { opacity: 1, duration: 0.2 }, i + 0.5);
          if (i < STEPS.length - 1) {
            // Dim the node back down as the packet moves on.
            tl!.to(
              node,
              { borderColor: NODE_BORDER, backgroundColor: NODE_BG, duration: 0.25 },
              i + 0.85,
            );
          }
          if (i > 0) {
            tl!.to(copies[i - 1], { opacity: 0, duration: 0.18 }, i - 0.05);
            tl!.to(copies[i], { opacity: 1, duration: 0.22 }, i + 0.12);
          }
        });
      });

      document.fonts.ready.then(build).catch(build);

      return () => {
        disposed = true;
        tl?.scrollTrigger?.kill();
        tl?.kill();
      };
    },
    { dependencies: [enhanced], scope: trackRef, revertOnUpdate: true },
  );

  return (
    <SectionShell id="ref-route-story" band="dark" decoAvoid className="max-md:bg-[#14141c]">
      <SectionHead
        center
        tone="light"
        gridFill={DARK_BAND}
        eyebrow="Follow one visitor"
        title={
          <>
            Watch a click <GradText>find its way.</GradText>
          </>
        }
        lead="One Android visitor from Berlin, 23:12 on a Tuesday, taps your link. Four rules look at the request — this is the story of every click, in slow motion."
      />

      <div ref={trackRef} className={`relative ${enhanced ? "h-[300vh]" : ""}`}>
        <div className={enhanced ? "sticky top-0 flex h-screen items-center" : ""}>
          <div className="grid w-full items-center gap-12 md:grid-cols-2">
            {/* Copy panel — crossfades per step when enhanced, stacks otherwise. */}
            <div ref={copyRef} className="relative">
              {STEPS.map((s, i) => (
                <div
                  key={s.id}
                  data-story-copy
                  className={enhanced && i > 0 ? "absolute inset-0" : enhanced ? "" : "mb-10"}
                >
                  <div className="font-jetbrains text-[13px] tracking-[0.08em] text-white/45">
                    {String(i + 1).padStart(2, "0")} / 04
                  </div>
                  <h3 className="mt-3 text-[clamp(22px,2.4vw,30px)] font-bold tracking-[-0.02em] text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 max-w-[440px] text-[15.5px] leading-[1.65] text-white/70">
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* The rule chain. Own opaque surfaces — NOT grid blocks (sticky). */}
            <div ref={chainRef} className="relative">
              <div className="mb-5 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.07] px-4 py-2 font-jetbrains text-[13px] text-white/85">
                <span className="h-2 w-2 rounded-full bg-brand-500 shadow-[0_0_8px_var(--color-brand-500)]" />
                Android · Berlin · Tue 23:12
              </div>

              <div className="relative flex flex-col gap-4">
                <div aria-hidden className="absolute bottom-6 left-[27px] top-2 w-px bg-white/15" />
                <div
                  aria-hidden
                  data-story-packet
                  className={`absolute left-[27px] top-0 z-10 h-3 w-3 -translate-x-1/2 rounded-full bg-brand-500 shadow-[0_0_16px_var(--color-brand-500)] ${
                    enhanced ? "" : "hidden"
                  }`}
                />
                {STEPS.map((s, i) => (
                  <div
                    key={s.id}
                    data-story-node
                    className="relative z-[1] flex items-center gap-4 rounded-xl border px-5 py-4"
                    style={{ borderColor: NODE_BORDER, backgroundColor: NODE_BG }}
                  >
                    <div className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-[10px] bg-white/[0.06] font-jetbrains text-[11px] text-white/60">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-jetbrains text-[12px] uppercase tracking-[0.09em] text-white/50">
                        {s.rule}
                      </div>
                      <div className="mt-0.5 truncate text-[15px] font-semibold text-white">
                        {s.match}
                      </div>
                    </div>
                    <div
                      data-story-verdict
                      className={`shrink-0 font-jetbrains text-[12px] ${
                        i === STEPS.length - 1 ? "text-brand-500" : "text-white/45"
                      }`}
                    >
                      {s.verdict}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
